import { create } from 'zustand';
import type {
  ConversationSurface,
  RecordContext,
} from '../types/assistant.types';

/**
 * Panel/session UI state ONLY. Chat state (messages, streaming, pending
 * confirmation) lives in the TanStack AI chat client via use-assistant-chat.
 */
/** No 'intakes'. Captured leads are inbox work and live on the conversations
 *  page; this panel never shows them. Adding the value back is what would let
 *  a stray setView('intakes') render an empty body again. */
export type AssistantView = 'list' | 'thread' | 'knowledge';

interface AssistantState {
  isOpen: boolean;
  view: AssistantView;
  activeConversationId: string | null;
  /** The conversation row the SERVER has. null until the first run of a new
   *  thread reports one — the client-side id above is only an SDK key. */
  serverConversationId: number | null;
  /** The open conversation's surface. WHATSAPP is read-only in the panel: the
   *  server refuses messages and confirmations on it. null for a new chat. */
  activeSurface: ConversationSurface | null;
  recordContext: RecordContext | null;
  contextDismissed: boolean;
  /** Whose store this is ("org:user"). Kept here, not in a component ref, so
   *  an identity change is seen even when the panel was unmounted meanwhile
   *  (logout -> login page -> login as someone else). */
  scope: string | null;
  /** Clear everything that belongs to one user's session. */
  reset: () => void;
  /** Adopt `scope`; if it differs from the previous non-null one, reset. */
  bindScope: (scope: string) => void;
  open: () => void;
  close: () => void;
  toggle: () => void;
  setView: (v: AssistantView) => void;
  /** Enter a thread. `null` = new conversation — an id is assigned up front
   *  so the SDK chat client (keyed on it) is stable for the whole thread. */
  startConversation: (
    id: number | null,
    surface?: ConversationSurface | null
  ) => void;
  /** Adopt the id the server assigned (from RUN_STARTED). */
  setServerConversationId: (id: number) => void;
  setRecordContext: (rc: RecordContext | null) => void;
  dismissContext: () => void;
}

const SESSION_STATE = {
  view: 'list' as AssistantView,
  activeConversationId: null,
  serverConversationId: null,
  activeSurface: null,
  recordContext: null,
  contextDismissed: false,
};

export const useAssistantStore = create<AssistantState>((set, get) => ({
  isOpen: false,
  view: 'thread',
  activeConversationId: null,
  serverConversationId: null,
  activeSurface: null,
  recordContext: null,
  contextDismissed: false,
  scope: null,

  reset: () => set({ ...SESSION_STATE, scope: null }),

  bindScope: (scope) => {
    const previous = get().scope;
    if (previous === scope) return;
    set(previous === null ? { scope } : { ...SESSION_STATE, scope });
  },

  open: () => set({ isOpen: true, view: 'thread' }),
  close: () => set({ isOpen: false }),
  toggle: () =>
    set((s) => ({
      isOpen: !s.isOpen,
      view: s.isOpen ? s.view : 'thread',
    })),

  setView: (view) => set({ view }),

  startConversation: (id, surface = null) =>
    set({
      activeConversationId: id === null ? crypto.randomUUID() : String(id),
      serverConversationId: id,
      activeSurface: id === null ? null : surface,
      view: 'thread',
    }),

  setServerConversationId: (id) =>
    set((s) =>
      s.serverConversationId === id ? s : { serverConversationId: id }
    ),

  setRecordContext: (rc) =>
    set((s) => ({
      recordContext: rc,
      contextDismissed:
        rc &&
        s.recordContext &&
        rc.id === s.recordContext.id &&
        rc.type === s.recordContext.type
          ? s.contextDismissed
          : false,
    })),

  dismissContext: () => set({ contextDismissed: true }),
}));
